import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { TrendingUp, Wallet, Users as UsersIcon, Activity } from "lucide-react";
import { Link } from "react-router-dom";

const fmtIDR = (n) => "Rp " + Math.round(n || 0).toLocaleString("id-ID");

function Stat({ label, value, icon: Icon, tint, testid, sub }) {
  return (
    <Card data-testid={testid} className="p-5 glass border-slate-800 hover-lift">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
          <div className="mt-2 font-mono text-2xl font-bold">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${tint}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { t, user } = useApp();
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setS(data));
  }, []);

  const daily = s?.daily_profit || [];
  const vol = s?.volume_by_currency || {};
  const volData = ["SAR", "IDR", "USDT"].map((k) => ({ currency: k, volume: vol[k]?.volume || 0, count: vol[k]?.count || 0 }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-sm text-slate-400">{t("welcome")},</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{user?.name}</h1>
        </div>
        <Link
          to="/new"
          data-testid="cta-new-tx"
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg"
        >
          + {t("new_transaction")}
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-today-profit" label={`${t("profit")} · ${t("today")}`} value={fmtIDR(s?.today_profit_idr)} icon={TrendingUp} tint="bg-emerald-500/15 text-emerald-400" />
        <Stat testid="stat-total-profit" label={`${t("profit")} · ${t("total")}`} value={fmtIDR(s?.total_profit_idr)} icon={Wallet} tint="bg-amber-500/15 text-amber-400" />
        <Stat testid="stat-today-tx" label={`${t("transactions")} · ${t("today")}`} value={s?.today_transactions ?? 0} sub={`${s?.total_transactions ?? 0} total`} icon={Activity} tint="bg-sky-500/15 text-sky-400" />
        <Stat testid="stat-customers" label={t("customers")} value={s?.customers_count ?? 0} icon={UsersIcon} tint="bg-rose-500/15 text-rose-400" />
      </div>

      <div className="grid lg:grid-cols-1 gap-4">
        <Card className="p-5 glass border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">{t("profit_7d")}</h3>
              <p className="text-xs text-slate-500">{t("daily_profit")} (IDR)</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ background: "#131822", border: "1px solid #283248", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="profit" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3, fill: "#F59E0B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5 glass border-slate-800">
        <h3 className="font-semibold mb-1">{t("volume")} · 7d</h3>
        <p className="text-xs text-slate-500 mb-4">By outgoing currency leg</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volData}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="currency" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ background: "#131822", border: "1px solid #283248", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="volume" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
